import { useState, useEffect } from 'react';
import axios from 'axios';
import { baseUrl, rozerpay_key_id } from '../config';

const M = '#7a0000';
const G = '#FFD700';

function loadRazorpay() {
  return new Promise(res => {
    if (window.Razorpay) return res(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

export default function QuickPay() {
  const params   = new URLSearchParams(window.location.search);
  const initPh   = params.get('ph') || '';
  const initTk   = params.get('tk') || '';
  const noToken  = !initPh || !initTk; // arrived via static WhatsApp button

  // Phone-entry state (used when no token in URL)
  const [phoneLookup,   setPhoneLookup]   = useState('');
  const [lookupOtp,     setLookupOtp]     = useState('');
  const [lookupOtpSent, setLookupOtpSent] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupErr,     setLookupErr]     = useState('');

  // Resolved ph/tk (either from URL or after phone+OTP lookup)
  const [ph, setPh] = useState(initPh);
  const [tk, setTk] = useState(initTk);

  const [info,         setInfo]         = useState(null);
  const [loadErr,      setLoadErr]      = useState('');
  const [loading,      setLoading]      = useState(!noToken); // skip loading if no token yet
  const [months,       setMonths]       = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [paying,       setPaying]       = useState(false);
  const [payErr,       setPayErr]       = useState('');
  const [success,      setSuccess]      = useState(null);
  const [otp,          setOtp]          = useState('');
  const [otpSent,      setOtpSent]      = useState(false);
  const [otpLoading,   setOtpLoading]   = useState(false);
  const [otpErr,       setOtpErr]       = useState('');

  useEffect(() => {
    if (!ph || !tk) return;
    setLoading(true);
    axios.get(`${baseUrl}/quick-pay/info?ph=${encodeURIComponent(ph)}&tk=${encodeURIComponent(tk)}`)
      .then(r => {
        if (r.data.status) {
          setInfo(r.data);
          setCustomAmount(String(r.data.announced_amount));
        } else setLoadErr(r.data.message || 'Link invalid.');
      })
      .catch(e => setLoadErr(e.response?.data?.message || 'Something went wrong.'))
      .finally(() => setLoading(false));
  }, [ph, tk]);

  // Phone-lookup: send OTP to entered number
  const handleSendLookupOtp = async () => {
    if (phoneLookup.length < 10) { setLookupErr('सही मोबाइल नंबर डालें।'); return; }
    setLookupLoading(true); setLookupErr('');
    try {
      const r = await axios.post(`${baseUrl}/member/send-otp`, { phone: phoneLookup });
      if (r.data.status) setLookupOtpSent(true);
      else setLookupErr(r.data.message || 'OTP नहीं भेजा जा सका।');
    } catch { setLookupErr('Something went wrong.'); }
    finally { setLookupLoading(false); }
  };

  // Phone-lookup: verify OTP → get token → load pay info
  const handleVerifyLookupOtp = async () => {
    setLookupLoading(true); setLookupErr('');
    try {
      const r = await axios.post(`${baseUrl}/quick-pay/generate-token`, { phone: phoneLookup, otp: lookupOtp });
      if (r.data.status && r.data.tk) {
        setPh(phoneLookup);
        setTk(r.data.tk);
      } else setLookupErr(r.data.message || 'Invalid OTP.');
    } catch { setLookupErr('OTP verification failed.'); }
    finally { setLookupLoading(false); }
  };

  const finalAmount = parseInt(customAmount) || (info?.announced_amount || 200);
  const total       = finalAmount * months;

  const handlePay = async () => {
    setPayErr(''); setPaying(true);
    try {
      const orderRes = await axios.post(`${baseUrl}/quick-pay/create-order`, {
        ph, tk, months, custom_amount: finalAmount,
      });
      if (!orderRes.data.status) { setPayErr(orderRes.data.message); setPaying(false); return; }

      await loadRazorpay();
      const { order_id, amount } = orderRes.data;

      new window.Razorpay({
        key:         rozerpay_key_id,
        amount:      amount * 100,
        currency:    'INR',
        name:        'ABRSVS — समर्पण',
        description: `समाज शिक्षा निधि — ${months} माह`,
        order_id,
        prefill:     { name: info.name, contact: ph },
        theme:       { color: M },
        handler: async (response) => {
          try {
            const vRes = await axios.post(`${baseUrl}/quick-pay/verify-payment`, {
              order_id,
              payment_id: response.razorpay_payment_id,
              signature:  response.razorpay_signature,
              ph, tk,
            });
            if (vRes.data.status) {
              setSuccess({ token: vRes.data.token, name: vRes.data.name, amount: vRes.data.amount, months: vRes.data.months });
            } else setPayErr(vRes.data.message || 'Verification failed.');
          } catch (e) { setPayErr(e.response?.data?.message || 'Verification error.'); }
          finally { setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
      }).open();
    } catch (e) { setPayErr(e.response?.data?.message || 'Error.'); setPaying(false); }
  };

  const handleLoginAfterPay = async () => {
    setOtpLoading(true); setOtpErr('');
    try {
      const r = await axios.post(`${baseUrl}/member/send-otp`, { phone: ph });
      if (r.data.status) setOtpSent(true);
      else setOtpErr(r.data.message);
    } catch { setOtpErr('OTP send failed.'); }
    finally { setOtpLoading(false); }
  };

  const handleVerifyOtp = async () => {
    setOtpLoading(true); setOtpErr('');
    try {
      const r = await axios.post(`${baseUrl}/member/verify-otp`, { phone: ph, otp });
      if (r.data.status && r.data.token) {
        localStorage.setItem('memberToken', r.data.token);
        localStorage.setItem('memberInfo', JSON.stringify(r.data.member || {}));
        window.location.href = '/user-portal';
      } else setOtpErr(r.data.message || 'Invalid OTP.');
    } catch { setOtpErr('OTP verification failed.'); }
    finally { setOtpLoading(false); }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const page = { minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: "'Noto Sans Devanagari', sans-serif" };
  const card = { background: '#fff', borderRadius: 18, width: '100%', maxWidth: 420, boxShadow: '0 4px 30px rgba(0,0,0,0.10)', overflow: 'hidden' };
  const header = { background: `linear-gradient(135deg, ${M}, #b71c1c)`, padding: '24px 20px', color: '#fff', textAlign: 'center' };

  // ── Phone-entry screen (static button — no token in URL) ────────────────────
  if (noToken && !ph && !tk) return (
    <div style={page}>
      <div style={card}>
        <div style={header}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>🙏</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>समर्पण करें</div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>ABRSVS — शिक्षा निधि</div>
        </div>
        <div style={{ padding: '24px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 16, textAlign: 'center' }}>
            अपना रजिस्टर्ड मोबाइल नंबर दर्ज करें
          </div>
          {!lookupOtpSent ? (
            <>
              <input
                type="tel" maxLength={10}
                value={phoneLookup}
                onChange={e => setPhoneLookup(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="मोबाइल नंबर (10 अंक)"
                style={{ width: '100%', height: 46, borderRadius: 10, border: '1.5px solid #d1d5db', padding: '0 14px', fontSize: 16, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
              />
              {lookupErr && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{lookupErr}</div>}
              <button onClick={handleSendLookupOtp} disabled={lookupLoading || phoneLookup.length < 10}
                style={{ width: '100%', height: 46, background: phoneLookup.length < 10 ? '#e5e7eb' : M, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: phoneLookup.length < 10 ? 'not-allowed' : 'pointer' }}>
                {lookupLoading ? 'OTP भेज रहे हैं…' : 'OTP भेजें'}
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, textAlign: 'center' }}>
                {phoneLookup} पर OTP भेजा गया
              </div>
              <input
                type="tel" maxLength={6}
                value={lookupOtp}
                onChange={e => setLookupOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6 अंकों का OTP"
                style={{ width: '100%', height: 46, borderRadius: 10, border: '1.5px solid #d1d5db', padding: '0 14px', fontSize: 20, letterSpacing: 6, outline: 'none', boxSizing: 'border-box', marginBottom: 12, textAlign: 'center' }}
              />
              {lookupErr && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{lookupErr}</div>}
              <button onClick={handleVerifyLookupOtp} disabled={lookupLoading || lookupOtp.length < 4}
                style={{ width: '100%', height: 46, background: lookupOtp.length < 4 ? '#e5e7eb' : M, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: lookupOtp.length < 4 ? 'not-allowed' : 'pointer' }}>
                {lookupLoading ? 'Verifying…' : 'Verify करें'}
              </button>
              <button onClick={() => { setLookupOtpSent(false); setLookupOtp(''); setLookupErr(''); }}
                style={{ width: '100%', marginTop: 8, background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>
                ← नंबर बदलें
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Loading / Error ──────────────────────────────────────────────────────────
  if (loading) return (
    <div style={page}>
      <div style={{ ...card, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⌛</div>
        <div style={{ color: '#6b7280' }}>Loading...</div>
      </div>
    </div>
  );

  if (loadErr) return (
    <div style={page}>
      <div style={{ ...card, padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>Link Invalid</div>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{loadErr}</div>
      </div>
    </div>
  );

  // ── Success screen ───────────────────────────────────────────────────────────
  if (success) return (
    <div style={page}>
      <div style={card}>
        <div style={{ ...header, background: 'linear-gradient(135deg,#14532d,#15803d)' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🙏</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>समर्पण सफल!</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>
            ₹{Number(success.amount).toLocaleString('en-IN')} — {success.months} माह
          </div>
        </div>
        <div style={{ padding: '24px 20px' }}>
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 16px', marginBottom: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: '#15803d', fontWeight: 700 }}>नमस्ते {success.name} जी!</div>
            <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>आपका समर्पण दर्ज हो गया। जय हो! 🏆</div>
          </div>

          {!otpSent ? (
            <>
              <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 16 }}>
                Portal में login करके receipt और payment history देखें
              </div>
              <button onClick={handleLoginAfterPay} disabled={otpLoading}
                style={{ width: '100%', background: M, color: G, border: 'none', borderRadius: 10, padding: 14, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                {otpLoading ? 'OTP भेज रहे हैं...' : '📱 OTP से Login करें'}
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 8, fontWeight: 600 }}>
                {ph} पर OTP भेजा गया
              </div>
              <input
                type="tel" maxLength={6} value={otp} onChange={e => setOtp(e.target.value)}
                placeholder="6-digit OTP"
                style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 10, padding: '12px 14px', fontSize: 18, textAlign: 'center', letterSpacing: 8, boxSizing: 'border-box', marginBottom: 12 }}
              />
              {otpErr && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{otpErr}</div>}
              <button onClick={handleVerifyOtp} disabled={otpLoading || otp.length < 4}
                style={{ width: '100%', background: M, color: G, border: 'none', borderRadius: 10, padding: 14, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                {otpLoading ? 'Verifying...' : '✓ OTP Verify करें → Portal खोलें'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Already paid ────────────────────────────────────────────────────────────
  if (info.already_paid) return (
    <div style={page}>
      <div style={card}>
        <div style={header}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>इस माह समर्पण हो चुका है</div>
        </div>
        <div style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#374151', marginBottom: 20 }}>
            {info.name} जी, आपने इस माह ₹{Number(info.paid_amount).toLocaleString('en-IN')} का समर्पण दिया है। 🙏
          </div>
          <button onClick={() => window.location.href = '/user-portal'}
            style={{ background: M, color: G, border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
            Portal खोलें →
          </button>
        </div>
      </div>
    </div>
  );

  // ── Payment form ─────────────────────────────────────────────────────────────
  const now = new Date();
  const monthName = now.toLocaleString('hi-IN', { month: 'long', year: 'numeric' });

  return (
    <div style={page}>
      <div style={card}>
        {/* Header */}
        <div style={header}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            ABRSVS — समाज शिक्षा निधि
          </div>
          <div style={{ fontSize: 22, fontWeight: 900 }}>नमस्ते, {info.name} जी! 🙏</div>
          {info.village && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>📍 {info.village}</div>}
          <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 14px', display: 'inline-block' }}>
            <span style={{ fontSize: 12, color: G, fontWeight: 700 }}>{monthName} माह का समर्पण</span>
          </div>
        </div>

        {/* Form */}
        <div style={{ padding: '20px 20px 24px' }}>
          {/* Amount */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>मासिक राशि (₹)</div>
            <input
              type="number" min={100} value={customAmount}
              onChange={e => setCustomAmount(e.target.value)}
              style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: 10, padding: '10px 14px', fontSize: 18, fontWeight: 700, boxSizing: 'border-box' }}
            />
          </div>

          {/* Months */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 8 }}>कितने माह का समर्पण?</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
              {[1,3,6,12].map(m => (
                <button key={m} onClick={() => setMonths(m)}
                  style={{ padding: '10px 4px', borderRadius: 10, border: `2px solid ${months === m ? M : '#e5e7eb'}`,
                    background: months === m ? M : '#fff', color: months === m ? G : '#374151',
                    fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
                  <div>{m} माह</div>
                  {finalAmount > 0 && <div style={{ fontSize: 10, marginTop: 2, opacity: 0.8 }}>₹{(finalAmount*m).toLocaleString('en-IN')}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Total */}
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>कुल राशि</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: M }}>₹{total.toLocaleString('en-IN')}</span>
          </div>

          {payErr && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{payErr}</div>}

          <button onClick={handlePay} disabled={paying || total < 100}
            style={{ width: '100%', background: paying ? '#9ca3af' : M, color: G, border: 'none', borderRadius: 12,
              padding: '16px', fontSize: 15, fontWeight: 900, cursor: paying ? 'not-allowed' : 'pointer',
              boxShadow: paying ? 'none' : `0 4px 20px ${M}50` }}>
            {paying ? '⌛ Processing...' : `💳 ₹${total.toLocaleString('en-IN')} समर्पण करें`}
          </button>

          <div style={{ textAlign: 'center', fontSize: 10, color: '#9ca3af', marginTop: 12 }}>
            Secured by Razorpay • समर्पण सुरक्षित है
          </div>
        </div>
      </div>
    </div>
  );
}
